/**
 * The map: MapLibre GL JS, globe at low zoom, OpenFreeMap vector tiles. Every entity is a GeoJSON
 * source refreshed from the store; missiles are animated client-side along the great circle from
 * the position/time the server last gave, exactly as the original client did.
 *
 * Also here: clustering of structures with a stack picker for overlapping markers, blast/EMP area
 * rings at every missile's destination, 3-D site extrusions, and theme-driven basemap styles.
 */

import maplibregl, { type Map as MLMap } from "maplibre-gl";
import { world, missileType } from "./store.svelte";
import { ArcsLayer } from "./arcs-gl";
import { ModelsLayer, MODEL_MIN_ZOOM } from "./models-gl";
import { markersOverModels } from "./layer-order";
import { poleCross, rotatedPanY } from "./pole";

const R = 6372.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

export interface Pos { lat: number; lon: number }

export function distanceKm(a: Pos, b: Pos) {
  const la1 = rad(a.lat), la2 = rad(b.lat), dLat = rad(b.lat - a.lat), dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(la1) * Math.cos(la2);
  return R * 2 * Math.asin(Math.sqrt(h));
}
function bearing(a: Pos, b: Pos) {
  const la1 = rad(a.lat), la2 = rad(b.lat), dLon = rad(b.lon - a.lon);
  return Math.atan2(Math.sin(dLon) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon));
}
export function move(a: Pos, brg: number, km: number): Pos {
  const ad = km / R, la1 = rad(a.lat), lo1 = rad(a.lon);
  const la2 = Math.asin(Math.sin(la1) * Math.cos(ad) + Math.cos(la1) * Math.sin(ad) * Math.cos(brg));
  let lo2 = lo1 + Math.atan2(Math.sin(brg) * Math.sin(ad) * Math.cos(la1), Math.cos(ad) - Math.sin(la1) * Math.sin(la2));
  if (lo2 > Math.PI) lo2 -= 2 * Math.PI; else if (lo2 <= -Math.PI) lo2 += 2 * Math.PI; // stay in (-180, 180]
  return { lat: deg(la2), lon: deg(lo2) };
}
function circle(c: Pos, km: number, n = 32): number[][] {
  const ring = [];
  for (let i = 0; i <= n; i++) { const p = move(c, (i / n) * 2 * Math.PI, km); ring.push([p.lon, p.lat]); }
  return ring;
}
/** Great-circle polyline: points sampled along the geodesic, so long shots bow on a flat map. */
function arc(a: Pos, b: Pos, n = 48): number[][] {
  const d = distanceKm(a, b), brg = bearing(a, b), out = [];
  for (let i = 0; i <= n; i++) { const p = move(a, brg, (d * i) / n); out.push([p.lon, p.lat]); }
  return out;
}
function square(c: Pos, halfKm: number): number[][] {
  return [0, 1, 2, 3, 0].map((i) => { const p = move(c, Math.PI / 4 + (i * Math.PI) / 2, halfKm * Math.SQRT2); return [p.lon, p.lat]; });
}
/** Map labels only: drop emoji and other astral-plane characters — the font server has no glyphs for them and every range costs a 404 + a local-render fallback. */
const mapText = (t: string) => t.replace(/[\u{10000}-\u{10FFFF}]/gu, "").replace(/\s+/g, " ").trim();
const pt = (p: Pos, props: any) => ({ type: "Feature", geometry: { type: "Point", coordinates: [p.lon, p.lat] }, properties: props });
const poly = (ring: number[][], props: any) => ({ type: "Feature", geometry: { type: "Polygon", coordinates: [ring] }, properties: props });
const fc = (features: any[]) => ({ type: "FeatureCollection", features });
const STRUCT_NAME: Record<string, string> = { missileSite: "missile site", samSite: "SAM site", sentryGun: "sentry gun", oreMine: "ore mine" };

export type Selection = { kind: "player" | "structure" | "missile" | "loot" | "blueprint" | "ground"; id?: number; pos?: Pos } | null;
export interface PickItem { kind: "player" | "structure" | "missile" | "interceptor" | "loot" | "blueprint"; id: number; pos: Pos; label: string; sub: string; color: string; distanceM: number; target?: Pos; icon?: string }

/** Basemap styles come through our own /map/ proxy+cache (see server/src/tiles.ts), never straight from OpenFreeMap. */
export const STYLES: Record<string, string> = {
  light: `${location.origin}/map/styles/liberty`,
  dark: `${location.origin}/map/styles/fiord`,
  retro: `${location.origin}/map/styles/fiord`, // dark ground under the phosphor filter — the WOPR look, not a green daylight map
  win31: `${location.origin}/map/styles/liberty`, // daylight map under the Program Manager (Cam, 2026-09-04)
};

/**
 * The basemaps a player can pick between, independent of the UI theme (Cam, 2026-09-05: "I need
 * some maps with city streets. add a map tab to the menu").
 *
 * `theme` still decides the app's own skin — the 1983 phosphor filter, the Windows 3.1 chrome — and
 * still supplies a DEFAULT basemap. Choosing a map here overrides only the ground, so wanting
 * legible streets no longer means giving up the theme you like.
 *
 * Every id is confirmed served by the tile proxy; `toner` is not (404) and is deliberately absent.
 */
export interface BasemapDef { id: string; label: string; blurb: string; dark: boolean }
export const BASEMAPS: BasemapDef[] = [
  { id: "bright",   label: "Bright",   blurb: "Street map — the most legible for driving", dark: false },
  { id: "liberty",  label: "Liberty",  blurb: "Detailed daylight streets",                 dark: false },
  { id: "positron", label: "Positron", blurb: "Pale and quiet; markers stand out",          dark: false },
  { id: "dark",     label: "Dark",     blurb: "Night streets, still readable",              dark: true },
  { id: "fiord",    label: "Fiord",    blurb: "Deep blue-grey, the 2030 look",              dark: true },
  // Mars ground (Cam, 2026-09-07). Both come from the same derived style in server/src/tiles.ts.
  // `mars-` keeps Martian ground at every zoom; `marsfade-` cross-fades to the real map as you zoom
  // in, which is the "globe is Mars, flat map is your streets" split.
  { id: "marsfade-bright", label: "Mars",         blurb: "Mars from orbit, your real streets up close", dark: false },
  { id: "mars-bright",     label: "Mars surface", blurb: "Real streets drawn straight on Martian ground", dark: true },
];
export const basemapUrl = (id: string) => `${location.origin}/map/styles/${id}`;
/** The style a settings object actually resolves to: an explicit basemap wins, else the theme's. */
export const styleFor = (s: { theme: string; basemap?: string | null }) =>
  s.basemap ? basemapUrl(s.basemap) : (STYLES[s.theme] ?? STYLES.light);
/**
 * The owner's local window (PLAN.md §7 v4 §3): inside it the basemap is the player's REAL streets,
 * so gold and sites drawn in game coordinates land on roads they can actually drive.
 *
 * All this side does is redirect the tile request. The server holds the rotation and does the
 * transform, so the client never learns it and there is no client-side geometry to get wrong —
 * which is the whole difference from the design this replaced.
 */
const Z_LOCAL = 10;   // below this a phone screen is wider than the window, so the seam is on-screen
const WINDOW_KM = 80; // ~50 miles, the same window the server uses for gold visibility
let localWin: { pos: Pos; token: string } | null = null;
/** Rewrites since load. The local window had no observable state at all, so "it stopped working"
 * could only ever be reported as an impression. Surfaced in the Maps tab. */
let localRewrites = 0;
export const localWindowStatus = () => ({ on: !!localWin, rewrites: localRewrites });

/** Centre of tile z/x/y, for deciding whether it falls inside the window. */
function tileCentre(z: number, x: number, y: number): Pos {
  const n = 2 ** z;
  return { lon: ((x + 0.5) / n) * 360 - 180, lat: deg(Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / n)))) };
}

export interface MapSettings { theme: "dark" | "light" | "retro" | "win31"; basemap?: string | null; clusterRadius: number; trail: "rainbow" | "classic"; lowDetail: boolean }
export interface MapFilters { missileSite: boolean; samSite: boolean; sentryGun: boolean; oreMine: boolean; players: boolean; missiles: boolean; interceptors: boolean; arcs: boolean; tracks: boolean; loot: boolean; radiation: boolean; labels: boolean }
export const ALL_FILTERS: MapFilters = { missileSite: true, samSite: true, sentryGun: true, oreMine: true, players: true, missiles: true, interceptors: true, arcs: true, tracks: true, loot: true, radiation: true, labels: true };

/**
 * Player discs: everyone blue, you yellow (Cam, 2026-09-11: "Make all the player circles blue, make
 * own player circle yellow").
 *
 * Keyed off the `rel` property the feature already carries, NOT off `relColor()` — that function
 * also drives structures, range rings and the selection card, so rewriting it would have recoloured
 * all of those too. Only the discs were asked for.
 *
 * The blue is the existing ally blue rather than a new one, so the map keeps one palette.
 *
 * ⚠️ This deliberately removes ally/enemy from the discs — every other player is now the same blue.
 * `rel` is untouched in the data, so the selection card still says which they are.
 */
const PLAYER_BLUE = "#4aa8ff";
const PLAYER_YELLOW = "#ffd23f";
const PLAYER_DISC: any = ["case", ["==", ["get", "rel"], "mine"], PLAYER_YELLOW, PLAYER_BLUE];

const LAYERS = ["radiation", "radiation-line", "radiation-icon", "aoe", "aoe-line", "rings", "selection", "arcs", "blueprints", "blueprints-icon", "clusters-stack2", "clusters-stack1", "clusters", "cluster-count", "cl-ms", "cl-sam", "cl-sg", "cl-ore", "cl-mis", "cl-int", "structures-outline", "structures", "structures-icon", "structures-state", "structures-label", "loots", "players-outline", "players", "players-icon", "players-label", "projectile-clusters", "projectile-count", "missiles", "interceptors", "selected-glow", "selected-marker", "selected-icon", "selected-label"];
const SOURCES = ["radiation", "radiation-centres", "aoe", "rings", "arcs", "structures", "blueprints", "loots", "players", "projectiles", "selection", "selected"];
/** The original client's marker art — 64 px white line icons, transparent, registered at 2× so they draw at 32 CSS px. */
const ICONS = ["marker_player", "marker_player_dead", "marker_missilesite", "marker_missilesitenuke", "marker_samsite", "marker_sentry", "marker_oremine",
  "marker_missile", "marker_missilenuke", "marker_ecm", "marker_interceptor", "marker_loot", "radiation_trefoil", "overlay_booting", "overlay_offline", "overlay_selling", "overlay_online"];

export class GameMap {
  map: MLMap;
  onSelect: (s: Selection) => void = () => {};
  onPick: (items: PickItem[], at: { x: number; y: number }, zoomTo: (() => void) | null) => void = () => {};
  private raf = 0;
  private ready = false;
  private settings: MapSettings;
  /** MapLibre custom WebGL layer: altitude arcs, rockets in the air, chase lines (see arcs-gl.ts). */
  arcs: ArcsLayer;
  /** three.js custom layer: 3-D player characters (see models-gl.ts). */
  models: ModelsLayer;
  /**
   * Let a globe pan carry over the poles instead of stopping dead (Cam, 2026-09-07).
   *
   * Wraps the camera helper's pan rather than replacing the drag handler, so MapLibre keeps doing
   * all of the panning and this only adds the crossing at the moment it would otherwise stop. See
   * pole.ts for why crossing beats trying to defeat the latitude clamp.
   *
   * Re-applied rather than done once: `migrateProjection` swaps the camera helper when the
   * projection changes, which would silently discard the patch.
   */
  private patchPoleCrossing() {
    const helper = (this.map as any).cameraHelper;
    if (!helper || helper.__nukiPole || !helper.useGlobeControls) return; // mercator already pans fine
    const orig = helper.handleMapControlsPan?.bind(helper);
    if (!orig) return; // a MapLibre that shapes this differently: leave the map alone rather than guess
    helper.handleMapControlsPan = (deltas: any, tr: any, preZoom: any) => {
      const p = deltas?.panDelta;
      if (p) {
        const cross = poleCross(tr.center.lat, tr.center.lng, tr.bearing, rotatedPanY(p.x, p.y, tr.bearing));
        // Cross BEFORE the pan: with the bearing flipped, MapLibre's own pan then carries the same
        // finger motion down the far side, so there is no second special case to maintain.
        if (cross) { tr.setCenter(new maplibregl.LngLat(cross.lon, tr.center.lat)); tr.setBearing(cross.bearing); }
      }
      return orig(deltas, tr, preZoom);
    };
    helper.__nukiPole = true;
  }

  /** Bumped on every style.load; a handler whose generation is stale must not touch the map. */
  private styleGen = 0;
  private overlayErr = false;
  overlayDrawn = 0; // arcs drawn in the last frame — for the [perf] readout
  private lastOverlayAt = 0;
  loaded = false;
  /** Camera follows a missile until the user moves the map themselves. */
  following: number | null = null;
  onFollowEnd: () => void = () => {};
  follow(missileId: number | null) { this.following = missileId; }
  /** Follow me: keep the map on the player as they move (Travel Mode). Ends on any gesture, like a missile follow. */
  followMe = false;
  onFollowMeEnd: () => void = () => {};
  private lastMeKey = "";
  setFollowMe(on: boolean) {
    this.followMe = on; this.lastMeKey = "";
    if (on && world.me?.pos) { this.map.easeTo({ center: [world.me.pos.lon, world.me.pos.lat], zoom: Math.max(this.map.getZoom(), 13), duration: 600 }); this.lastMeKey = `${world.me.pos.lat},${world.me.pos.lon}`; }
  }
  private followMeTick() {
    const p = world.me?.pos;
    if (!p) return;
    const key = `${p.lat},${p.lon}`;
    if (key === this.lastMeKey) return;
    this.lastMeKey = key;
    if (!this.map.isMoving()) this.map.easeTo({ center: [p.lon, p.lat], duration: 800 });
  }
  private endFollows() {
    if (this.following !== null) { this.following = null; this.onFollowEnd(); }
    if (this.followMe) { this.followMe = false; this.onFollowMeEnd(); }
  }
  private followTick() {
    const m = this.following !== null ? world.missiles.get(this.following) : null;
    if (!m) { this.following = null; this.onFollowEnd(); return; }
    const f = missileFlight(m);
    if (f && !this.map.isZooming() && !this.map.isRotating()) this.map.jumpTo({ center: [f.pos.lon, f.pos.lat] }); // a jump would cancel a +/− zoom ease
  }
  private viewDirty = true;
  filters: MapFilters = { ...ALL_FILTERS };
  setFilters(f: MapFilters) {
    this.filters = { ...f };
    if (!this.ready) return;
    const vis = (layers: string[], on: boolean) => { for (const l of layers) if (this.map.getLayer(l)) this.map.setLayoutProperty(l, "visibility", on ? "visible" : "none"); };
    vis(["missiles", "aoe", "aoe-line"], f.missiles);
    vis(["arcs"], f.missiles && f.tracks !== false); // flat ground track under a rocket
    // altitude mode: the GPU sprite is the rocket, so the ground marker only exists to be clicked (Cam, 2026-09-03)
    if (this.map.getLayer("missiles")) this.map.setPaintProperty("missiles", "icon-opacity", f.arcs !== false && !this.settings.lowDetail ? 0 : 1); // ground/none: the marker IS the rocket, at sea level
    vis(["interceptors"], f.interceptors);
    vis(["projectile-clusters", "projectile-count", "cl-mis", "cl-int"], f.missiles || f.interceptors);
    // basemap text: every symbol layer that isn't ours
    for (const l of this.map.getStyle()?.layers ?? []) if (l.type === "symbol" && !LAYERS.includes(l.id)) this.map.setLayoutProperty(l.id, "visibility", f.labels === false ? "none" : (/country/i.test(l.id) && !this.countryLabels ? "none" : "visible"));
    vis(["loots"], f.loot);
    // players are filtered in the data so that you yourself always stay on the map (Cam, 2026-09-03)
    vis(["radiation", "radiation-line", "radiation-icon"], f.radiation);
    this.refresh(true); // structures are filtered in the data so cluster counts stay honest
  }
  private lastProjectiles = 0;
  /** Client-side cost readout: ms spent per second in each phase, plus fps. */
  perf: { fps: number; refreshMs: number; flightsMs: number; animateMs: number; overlayMs?: number; frames: number; since: number } = { fps: 0, refreshMs: 0, flightsMs: 0, animateMs: 0, overlayMs: 0, frames: 0, since: performance.now() };

  constructor(container: HTMLElement, center: Pos, settings: MapSettings) {
    this.settings = { ...settings }; // a copy — the caller mutates its object before calling applySettings
    this.map = new maplibregl.Map({
      container,
      style: styleFor(settings),
      center: [center.lon, center.lat],
      zoom: 11,
      hash: true, // #zoom/lat/lon in the URL — shareable positions
      attributionControl: { compact: true },
      // Redirect basemap tiles inside the local window to this player's own transformed ones.
      // MapLibre calls this synchronously, so the token is held rather than fetched here.
      transformRequest: (url: string, kind?: string) => {
        if (kind !== "Tile" || !localWin) return { url };
        const m = /\/map\/planet\/[^/]+\/(\d+)\/(\d+)\/(\d+)\.pbf/.exec(url);
        if (!m) return { url };
        const z = +m[1], x = +m[2], y = +m[3];
        if (z < Z_LOCAL) return { url };
        // Measure to the tile's NEAREST part, not its centre. At Z_LOCAL a tile is ~40 km across, so
        // centre-only membership cuts the local area short (or overshoots) by up to half a tile —
        // a ~20 km error in where the real streets stop.
        const c = tileCentre(z, x, y);
        const halfDiagKm = (40075 / 2 ** z) * Math.cos(c.lat * Math.PI / 180) * 0.71;
        if (distanceKm(c, localWin.pos) - halfDiagKm > WINDOW_KM) return { url };
        localRewrites++;
        return { url: `${location.origin}/map/local/${z}/${x}/${y}.pbf?token=${encodeURIComponent(localWin.token)}` };
      },
    });
    this.map.on("moveend", () => { this.viewDirty = true; this.refreshFlights(); this.refreshRings(); this.syncModels(); });
    // any user gesture ends a follow
    // any user gesture ON THE MAP ends a follow; the +/− and compass buttons are not a gesture (Cam, 2026-09-02)
    for (const ev of ["dragstart", "wheel", "pitchstart", "rotatestart", "zoomstart"] as const) this.map.on(ev, (e: any) => {
      const oe = e?.originalEvent; if (!oe) return;
      const t = oe.target; if (t instanceof Element && t.closest(".maplibregl-ctrl")) return;
      this.endFollows();
    });
    (globalThis as any).__gmap = this; // debugging handle
    this.arcs = new ArcsLayer();
    this.models = new ModelsLayer();
    // ~2 MB of GLB lands well after the first draw. Until it does, `instances` is 0 and syncModels
    // leaves the flat sprites up; nothing used to re-run it afterwards, so whether the character or
    // the sprite won was decided by the HTTP cache and changed on every reload.
    this.models.onReady = () => { if (this.ready) { this.enforceModelOrder(); this.syncModels(); } };
    this.arcs.spritesOff = !this.models.off; // the 3-D layer draws the rockets in the air (Cam, 2026-09-03)
    this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
    this.map.addControl(new maplibregl.GlobeControl(), "top-right");
    this.map.on("style.load", async () => {
      // This handler AWAITS in the middle, so a style swap during that window starts a second copy
      // against the same map. Whichever resumes last then re-adds layers that already exist,
      // MapLibre throws on the duplicate id, and everything after the throw — the custom layers and
      // their ordering — silently never happens. Only the newest load owns the map.
      const gen = ++this.styleGen;
      this.map.setProjection({ type: "globe" });
      if (!this.countryLabels) for (const l of this.map.getStyle()?.layers ?? []) if (/country/i.test(l.id)) this.map.setLayoutProperty(l.id, "visibility", "none");
      await this.loadIcons(); // a style swap drops images; reload them before the layers that use them
      if (gen !== this.styleGen) return;
      this.addLayers();
      if (!this.arcs.off) this.map.addLayer(this.arcs as any, "projectile-clusters"); // a style swap drops custom layers too; below the models so rockets draw over their trajectories
      if (!this.models.off) { this.addModelsLayer(); }
      this.ready = true;
      this.setFilters(this.filters);
    });
    this.map.on("load", () => { this.loaded = true; });
    // the camera helper is swapped when the projection changes, taking the patch with it
    this.map.on("movestart", () => this.patchPoleCrossing());
    this.map.on("projectiontransition", () => this.patchPoleCrossing());
    this.animate(); // frame loop runs from the start; everything inside guards on this.ready
    this.map.on("click", (e) => this.handleClick(e));
  }

  /** A signed-distance-field octagon, so groups of sites get an octagon bubble with a tintable colour. */
  private addOctagon() {
    if (this.map.hasImage("octagon")) return;
    const N = 64, r = 24, c = N / 2, range = 8;
    const data = new Uint8ClampedArray(N * N * 4);
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
      const px = Math.abs(x + 0.5 - c), py = Math.abs(y + 0.5 - c);
      const d = Math.max(Math.max(px, py), (px + py) / Math.SQRT2) - r; // >0 outside a regular octagon
      const a = Math.max(0, Math.min(1, 0.5 - d / range));
      const i = (y * N + x) * 4; data[i] = data[i + 1] = data[i + 2] = 255; data[i + 3] = Math.round(a * 255);
    }
    this.map.addImage("octagon", { width: N, height: N, data }, { sdf: true, pixelRatio: 1 });
  }

  private async loadIcons() {
    this.addOctagon();
    await Promise.all(ICONS.map(async (name) => {
      if (this.map.hasImage(name)) return;
      try { const r = await this.map.loadImage(`/art/${name}.png`); if (!this.map.hasImage(name)) this.map.addImage(name, r.data, { pixelRatio: 1 } /* native px: every symbol doubled (Cam, 2026-09-02) */); } catch { /* missing art: layer falls back to nothing */ }
    }));
  }

  // ---- layers -------------------------------------------------------------------------------------

  private src(id: string, extra: any = {}) { if (!this.map.getSource(id)) this.map.addSource(id, { type: "geojson", data: fc([]) as any, ...extra }); }
  private set(id: string, data: any) { (this.map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(data); }

  private addLayers() {
    for (const s of SOURCES) {
      if (s === "structures") this.src(s, { cluster: this.settings.clusterRadius > 0, clusterRadius: Math.max(1, this.settings.clusterRadius), clusterMaxZoom: 15,
        clusterProperties: { mine: ["+", ["case", ["get", "mine"], 1, 0]], enemy: ["+", ["case", ["==", ["get", "rel"], "enemy"], 1, 0]], ally: ["+", ["case", ["==", ["get", "rel"], "ally"], 1, 0]],
          nMissileSite: ["+", ["case", ["==", ["get", "stype"], "missileSite"], 1, 0]], nSamSite: ["+", ["case", ["==", ["get", "stype"], "samSite"], 1, 0]], nSentryGun: ["+", ["case", ["==", ["get", "stype"], "sentryGun"], 1, 0]], nOreMine: ["+", ["case", ["==", ["get", "stype"], "oreMine"], 1, 0]] } });
      else if (s === "projectiles") this.src(s, { cluster: this.settings.clusterRadius > 0, clusterRadius: Math.max(1, this.settings.clusterRadius), clusterMaxZoom: 17,
        clusterProperties: { missiles: ["+", ["case", ["==", ["get", "kind"], "missile"], 1, 0]], interceptors: ["+", ["case", ["==", ["get", "kind"], "interceptor"], 1, 0]], hostile: ["+", ["case", ["get", "hostile"], 1, 0]] } });
      else if (s === "arcs") this.src(s, { lineMetrics: true }); // needed for line-gradient
      else this.src(s);
    }
    const m = this.map;
    // everything shrinks as you zoom out (Cam, 2026-09-02): one zoom factor multiplied into every size below
    // "zoom" may only feed a top-level interpolate, so the factor wraps the value rather than multiplying it
    const Z = (v: any): any => ["interpolate", ["linear"], ["zoom"], 0, ["*", 0.15, v], 2, ["*", 0.3, v], 6, ["*", 0.6, v], 10, ["*", 0.85, v], 12, v];
    m.addLayer({ id: "radiation", type: "fill", source: "radiation", paint: { "fill-color": "#7cff4a", "fill-opacity": 0.18 } });
    m.addLayer({ id: "radiation-line", type: "line", source: "radiation", paint: { "line-color": "#7cff4a", "line-width": 1, "line-dasharray": [2, 2] } });
    m.addLayer({ id: "aoe", type: "fill", source: "aoe", filter: ["!", ["get", "emp"]], paint: { "fill-color": ["get", "color"], "fill-opacity": 0.08 } });
    m.addLayer({ id: "aoe-line", type: "line", source: "aoe", paint: { "line-color": ["get", "color"], "line-width": ["case", ["get", "emp"], 1, 1.5], "line-opacity": ["case", ["get", "emp"], 0.5, 0.8], "line-dasharray": ["case", ["get", "emp"], ["literal", [3, 3]], ["literal", [1, 0]]] } });
    m.addLayer({ id: "rings", type: "line", source: "rings", paint: {
      "line-color": ["get", "color"], "line-width": 1,
      // a planned ring is dashed and fainter: same reach, but it is not there yet
      "line-opacity": ["case", ["==", ["get", "planned"], true], 0.38, 0.6],
      "line-dasharray": ["case", ["==", ["get", "planned"], true], ["literal", [2, 2]], ["literal", [1, 0]]],
    } });
    // the selected object's range ring sits under every marker (Cam, 2026-09-03: "range beneath everything")
    m.addLayer({ id: "selection", type: "line", source: "selection", paint: { "line-color": "#ffffff", "line-width": 2, "line-dasharray": [3, 2], "line-opacity": 0.8 } });
    // missile trails: a rainbow along the flight (launch → impact), or classic dark red / green for mine
    const rainbow = ["interpolate", ["linear"], ["line-progress"], 0, "#d500f9", 0.2, "#2979ff", 0.4, "#00e676", 0.6, "#ffea00", 0.8, "#ff9100", 1, "#ff1744"]; // launch cold → impact hot
    m.addLayer({ id: "arcs", type: "line", source: "arcs", layout: { "line-cap": "round", "line-join": "round" }, paint: this.settings.trail === "rainbow"
      ? { "line-gradient": rainbow as any, "line-width": 2, "line-opacity": 0.35 }
      : { "line-color": ["case", ["get", "mine"], "#3fd47a", "#8b0000"], "line-width": 2, "line-opacity": 0.35 } }); // ground track; the arc with altitude is on the overlay
    // groups of sites are octagons (Cam, 2026-09-02); projectile groups stay round so the two read apart
    // tiers, not counts: 30 px for 2–9, 36 for 10–49, 42 for 50–199, 48 for 200+ (octagon art is 48 px at size 1); still shrinks with zoom
    const tier: any = ["step", ["get", "point_count"], 0.625, 10, 0.75, 50, 0.875, 200, 1.0];
    const facColour: any = ["case", [">", ["get", "enemy"], 0], "#ff4d4d", [">", ["get", "mine"], 0], "#3fd47a", [">", ["get", "ally"], 0], "#4aa8ff", "#8b9bb0"];
    const facDark: any = ["case", [">", ["get", "enemy"], 0], "#7a1f1f", [">", ["get", "mine"], 0], "#1c5e37", [">", ["get", "ally"], 0], "#214a72", "#414b58"];
    // a cluster is a stack: two darker octagons peek out behind (only from 10 sites up for the second)
    m.addLayer({ id: "clusters-stack2", type: "symbol", source: "structures", filter: ["all", ["has", "point_count"], [">=", ["get", "point_count"], 10]],
      layout: { "icon-image": "octagon", "icon-size": Z(tier), "icon-offset": [14, 14], "icon-allow-overlap": true, "icon-ignore-placement": true },
      paint: { "icon-color": facDark, "icon-halo-color": "#0b0f14", "icon-halo-width": 2, "icon-opacity": 0.85 } });
    m.addLayer({ id: "clusters-stack1", type: "symbol", source: "structures", filter: ["has", "point_count"],
      layout: { "icon-image": "octagon", "icon-size": Z(tier), "icon-offset": [7, 7], "icon-allow-overlap": true, "icon-ignore-placement": true },
      paint: { "icon-color": facDark, "icon-halo-color": "#0b0f14", "icon-halo-width": 2, "icon-opacity": 0.9 } });
    m.addLayer({ id: "clusters", type: "symbol", source: "structures", filter: ["has", "point_count"],
      layout: { "icon-image": "octagon", "icon-size": Z(tier), "icon-allow-overlap": true, "icon-ignore-placement": true },
      paint: { "icon-color": facColour, "icon-halo-color": "#0b0f14", "icon-halo-width": 2, "icon-opacity": 0.95 } });
    m.addLayer({ id: "cluster-count", type: "symbol", source: "structures", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": Z(["step", ["get", "point_count"], 15, 10, 17, 50, 19, 200, 21]), "text-font": ["Noto Sans Bold"], "text-allow-overlap": true, "text-ignore-placement": true }, paint: { "text-color": "#ffffff", "text-halo-color": "#0b0f14", "text-halo-width": 1.2 } });
    // the types inside a group, as a row of the classic icons above the bubble. icon-offset can only be
    // data-driven through case/literal, so each layer enumerates the 16 present/absent patterns.
    const kinds: [string, string, string][] = [["nMissileSite", "marker_missilesite", "cl-ms"], ["nSamSite", "marker_samsite", "cl-sam"], ["nSentryGun", "marker_sentry", "cl-sg"], ["nOreMine", "marker_oremine", "cl-ore"]];
    const present = (k: string): any => [">", ["get", k], 0];
    kinds.forEach(([k, icon, id], idx) => {
      const branches: any[] = [];
      for (let mask = 0; mask < 16; mask++) {
        if (!(mask & (1 << idx))) continue; // this type absent → layer's filter hides it anyway
        const cond: any[] = ["all", ...kinds.map(([kk], j) => (mask & (1 << j)) ? present(kk) : ["==", ["get", kk], 0])];
        const count = [0, 1, 2, 3].filter((j) => mask & (1 << j)).length;
        const slot = [0, 1, 2, 3].filter((j) => j < idx && (mask & (1 << j))).length;
        const dx = 22 * (slot - (count - 1) / 2);
        branches.push(cond, ["literal", [dx / 0.5, -110]]); // offsets are in icon px before icon-size (0.5)
      }
      m.addLayer({ id, type: "symbol", source: "structures", filter: ["all", ["has", "point_count"], present(k)], layout: {
        "icon-image": icon, "icon-size": Z(0.5), "icon-allow-overlap": true, "icon-ignore-placement": true,
        "icon-offset": ["case", ...branches, ["literal", [0, -110]]] as any } });
    });
    const pk: [string, string, string][] = [["missiles", "marker_missile", "cl-mis"], ["interceptors", "marker_interceptor", "cl-int"]];
    pk.forEach(([k, icon, id], idx) => {
      const both: any = ["all", [">", ["get", "missiles"], 0], [">", ["get", "interceptors"], 0]];
      m.addLayer({ id, type: "symbol", source: "projectiles", filter: ["all", ["has", "point_count"], [">", ["get", k], 0]], layout: {
        "icon-image": icon, "icon-size": Z(0.5), "icon-allow-overlap": true, "icon-ignore-placement": true,
        "icon-offset": ["case", both, ["literal", [idx === 0 ? -28 : 28, -56]], ["literal", [0, -56]]] as any } });
    });
    // relationship halo under the classic white icon, so the art reads on any basemap and the colour still means something
    // dark 2 px outline, thin faction-coloured inner edge, muted faction fill — no white rings (Cam, 2026-09-03)
    // Plans: a dashed ghost ring, deliberately unlike a structure. Its own source, NOT the clustered
    // structures one — mixing them would put plans into structure cluster counts, which is exactly
    // the confusion the separate collection exists to prevent.
    m.addLayer({ id: "blueprints", type: "circle", source: "blueprints", paint: {
      "circle-radius": Z(26), "circle-color": "#4aa3ff", "circle-opacity": 0.12,
      "circle-stroke-width": 2, "circle-stroke-color": "#4aa3ff", "circle-stroke-opacity": 0.85 } });
    m.addLayer({ id: "blueprints-icon", type: "symbol", source: "blueprints", layout: {
      "icon-image": ["get", "icon"], "icon-size": Z(0.5), "icon-allow-overlap": true,
      "text-field": ["get", "label"], "text-size": 10, "text-offset": [0, 1.5], "text-anchor": "top",
      "text-font": ["Noto Sans Regular"], "text-allow-overlap": true,
    }, paint: { "icon-opacity": 0.65, "text-color": "#9fd0ff", "text-halo-color": "#0b0f14", "text-halo-width": 1 } });

    m.addLayer({ id: "structures", type: "circle", source: "structures", filter: ["!", ["has", "point_count"]], paint: {
      "circle-radius": Z(30), "circle-color": ["get", "color"], "circle-opacity": ["case", ["==", ["get", "state"], "online"], 0.55, 0.3],
      "circle-stroke-width": 2, "circle-stroke-color": ["get", "color"], "circle-stroke-opacity": ["case", ["==", ["get", "state"], "online"], 1, 0.6] } });
    m.addLayer({ id: "structures-outline", type: "circle", source: "structures", filter: ["!", ["has", "point_count"]], paint: {
      "circle-radius": Z(32), "circle-color": "rgba(0,0,0,0)", "circle-stroke-width": 2, "circle-stroke-color": "#0b0f14", "circle-stroke-opacity": 0.9 } }, "structures");
    m.addLayer({ id: "structures-icon", type: "symbol", source: "structures", filter: ["!", ["has", "point_count"]], layout: {
      "icon-image": ["match", ["get", "stype"], "missileSite", ["case", ["get", "nuclear"], "marker_missilesitenuke", "marker_missilesite"], "samSite", "marker_samsite", "sentryGun", "marker_sentry", "oreMine", "marker_oremine", "marker_missilesite"],
      "icon-size": Z(0.78), "icon-allow-overlap": true, "icon-ignore-placement": true } });
    m.addLayer({ id: "structures-state", type: "symbol", source: "structures", filter: ["all", ["!", ["has", "point_count"]], ["!=", ["get", "state"], "online"]], layout: {
      "icon-image": ["match", ["get", "state"], "booting", "overlay_booting", "offline", "overlay_offline", "selling", "overlay_selling", "overlay_online"],
      "icon-size": Z(0.7), "icon-offset": [32, -32], "icon-allow-overlap": true, "icon-ignore-placement": true } });
    m.addLayer({ id: "structures-label", type: "symbol", source: "structures", filter: ["!", ["has", "point_count"]], minzoom: 12, layout: { "text-field": ["get", "label"], "text-size": 10, "text-offset": [0, 1.3], "text-anchor": "top", "text-font": ["Noto Sans Regular"], "text-allow-overlap": true }, paint: { "text-color": "#e6edf3", "text-halo-color": "#0b0f14", "text-halo-width": 1 } });
    m.addLayer({ id: "loots", type: "symbol", source: "loots", layout: { "icon-image": "marker_loot", "icon-size": Z(0.45), "icon-allow-overlap": true, "icon-ignore-placement": true } });
    m.addLayer({ id: "radiation-icon", type: "symbol", source: "radiation-centres", layout: { "icon-image": "radiation_trefoil", "icon-size": Z(0.5), "icon-allow-overlap": true } });
    m.addLayer({ id: "players-outline", type: "circle", source: "players", paint: { "circle-radius": Z(30), "circle-color": "rgba(0,0,0,0)", "circle-stroke-width": 2, "circle-stroke-color": "#0b0f14" } });
    m.addLayer({ id: "players", type: "circle", source: "players", paint: { "circle-radius": Z(28), "circle-color": PLAYER_DISC, "circle-stroke-width": 2, "circle-stroke-color": PLAYER_DISC, "circle-opacity": ["case", ["get", "dead"], 0.3, 0.7] } });
    m.addLayer({ id: "players-icon", type: "symbol", source: "players", layout: { "icon-image": ["case", ["get", "dead"], "marker_player_dead", "marker_player"], "icon-size": Z(["case", ["get", "dead"], 0.6, 0.32]), "icon-allow-overlap": true, "icon-ignore-placement": true } });
    m.addLayer({ id: "players-label", type: "symbol", source: "players", minzoom: 5, layout: { "text-field": ["get", "name"], "text-size": 11, "text-offset": [0, 1.4], "text-anchor": "top", "text-font": ["Noto Sans Regular"], "text-allow-overlap": true }, paint: { "text-color": "#e6edf3", "text-halo-color": "#0b0f14", "text-halo-width": 1 } });
    m.addLayer({ id: "projectile-clusters", type: "circle", source: "projectiles", filter: ["has", "point_count"], paint: {
      "circle-radius": Z(["step", ["get", "point_count"], 15, 10, 18, 50, 21, 200, 24]),
      "circle-color": ["case", [">", ["get", "hostile"], 0], "#ff5a36", [">", ["get", "missiles"], 0], "#3fd47a", "#4aa8ff"],
      "circle-stroke-width": 2, "circle-stroke-color": "#0b0f14", "circle-opacity": 0.9 } });
    m.addLayer({ id: "projectile-count", type: "symbol", source: "projectiles", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": Z(15), "text-font": ["Noto Sans Bold"], "text-allow-overlap": true, "text-ignore-placement": true }, paint: { "text-color": "#ffffff", "text-halo-color": "#0b0f14", "text-halo-width": 1.2 } });
    m.addLayer({ id: "missiles", type: "symbol", source: "projectiles", filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "kind"], "missile"]], layout: {
      "icon-image": ["case", ["get", "nuclear"], "marker_missilenuke", ["get", "ecm"], "marker_ecm", "marker_missile"], "icon-size": Z(0.6), "icon-rotate": ["get", "rot"], "icon-rotation-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true },
      paint: { "icon-opacity": 0.45 } }); // the ground shadow; the flying missile is drawn on the overlay canvas
    m.addLayer({ id: "interceptors", type: "symbol", source: "projectiles", filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "kind"], "interceptor"]], layout: {
      "icon-image": "marker_interceptor", "icon-size": Z(0.8), "icon-rotate": ["get", "rot"], "icon-rotation-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true } });
    // the selected object, redrawn on top of the pile: outer glow, marker, icon, label
    m.addLayer({ id: "selected-glow", type: "circle", source: "selected", paint: { "circle-radius": Z(44), "circle-color": ["get", "color"], "circle-opacity": 0.28, "circle-blur": 0.6 } });
    m.addLayer({ id: "selected-marker", type: "circle", source: "selected", paint: { "circle-radius": Z(30), "circle-color": ["get", "color"], "circle-opacity": 0.95, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff" } });
    m.addLayer({ id: "selected-icon", type: "symbol", source: "selected", layout: { "icon-image": ["get", "icon"], "icon-size": Z(0.78), "icon-allow-overlap": true, "icon-ignore-placement": true } });
    m.addLayer({ id: "selected-label", type: "symbol", source: "selected", layout: { "text-field": ["get", "label"], "text-size": 13, "text-font": ["Noto Sans Bold"], "text-offset": [0, 1.6], "text-anchor": "top", "text-allow-overlap": true, "text-ignore-placement": true }, paint: { "text-color": "#ffffff", "text-halo-color": "#0b0f14", "text-halo-width": 1.5 } });
    // groups sit on top of the player standing in them; single structures stay under the player
    for (const id of ["clusters-stack2", "clusters-stack1", "clusters", "cluster-count", "cl-ms", "cl-sam", "cl-sg", "cl-ore"]) if (m.getLayer(id)) m.moveLayer(id, "projectile-clusters");
    // basemap as context, not competition: roads/boundaries and their text step back (Cam, 2026-09-03)
    for (const l of m.getStyle()?.layers ?? []) {
      if (LAYERS.includes(l.id) || l.id === "structures-outline" || l.id === "players-outline") continue;
      if (l.type === "line") m.setPaintProperty(l.id, "line-opacity", 0.55);
      else if (l.type === "symbol") { m.setPaintProperty(l.id, "text-opacity", 0.7); m.setPaintProperty(l.id, "icon-opacity", 0.7); }
    }
  }

  /** Theme or cluster setting changed: swap the basemap (which drops our layers) and rebuild. */
  applySettings(s: MapSettings) {
    const styleChanged = styleFor(s) !== styleFor(this.settings); // the resolved style, not the theme name
    this.models.setTheme(s);
    const detailChanged = s.lowDetail !== this.settings.lowDetail;
    this.settings = { ...s };
    this.ready = false;
    if (styleChanged) {
      this.map.setStyle(styleFor(s)); // style.load → addLayers
      // Re-request the window's tiles once the new style has settled. UNPROVEN as the cause of Cam's
      // 2026-09-05 report ("the map change overrode the local map thing") — transformRequest lives on
      // the map, not the style, and survives setStyle, which I checked in MapLibre 5.24's source. But
      // a style swap can carry over already-loaded tiles for an unchanged source, and every one of
      // these styles uses the same openmaptiles source, so in-window tiles fetched as world tiles
      // would persist. Cheap, and it closes that path whether or not it was the one taken.
      this.map.once("idle", () => { this.lastTileReloadAt = null; this.reloadBasemapTiles(); });
    }
    else {
      for (const l of LAYERS) if (this.map.getLayer(l)) this.map.removeLayer(l);
      if (this.map.getLayer("arcs-gl")) this.map.removeLayer("arcs-gl");
      if (this.map.getLayer("models-gl")) this.map.removeLayer("models-gl");
      for (const src of SOURCES) if (this.map.getSource(src)) this.map.removeSource(src);
      this.addLayers();
      if (!this.arcs.off) this.map.addLayer(this.arcs as any, "projectile-clusters");
      if (!this.models.off) { this.addModelsLayer(); }
      this.ready = true;
      this.refresh(true);
    }
  }

  // ---- clicking -----------------------------------------------------------------------------------

  /** Relationship colour — still drives structures, range rings and the selection card.
   *  The player DISCS deliberately no longer use it: see PLAYER_DISC. */
  relColor(ownerId: number): { color: string; rel: string } {
    const me = world.me;
    if (!me) return { color: "#8b9bb0", rel: "neutral" };
    if (ownerId === me.id) return { color: "#3fd47a", rel: "mine" };
    const o = world.players.get(ownerId);
    if (o && me.allianceId !== -1 && o.allianceId === me.allianceId && !o.requestingJoin) return { color: "#4aa8ff", rel: "ally" };
    return { color: "#ff4d4d", rel: "enemy" };
  }

  private async handleClick(e: maplibregl.MapMouseEvent) {
    const tol = (navigator.maxTouchPoints > 0 ? 24 : 16) / 2;
    const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [[e.point.x - tol, e.point.y - tol], [e.point.x + tol, e.point.y + tol]];
    const hits = this.map.queryRenderedFeatures(bbox, { layers: ["clusters", "projectile-clusters", "structures", "structures-icon", "blueprints", "blueprints-icon", "players", "players-icon", "loots", "missiles", "interceptors"].filter((l) => this.map.getLayer(l)) });
    const items: PickItem[] = [];
    const seen = new Set<string>();
    let zoomTo: (() => void) | null = null;
    // A group is one list. If the click touches any cluster bubble, the list is exactly that cluster's
    // members (the nearest bubble if several overlap) — never a mix of bubbles and loose markers.
    const clusters = hits.filter((f) => f.properties!.cluster);
    if (clusters.length) {
      const nearest = clusters.map((f) => { const c = (f.geometry as any).coordinates as [number, number]; const p = this.map.project(c); return { f, c, d: Math.hypot(p.x - e.point.x, p.y - e.point.y) }; })
        .sort((a, b) => a.d - b.d)[0];
      const src = this.map.getSource(nearest.f.source === "projectiles" ? "projectiles" : "structures") as maplibregl.GeoJSONSource;
      // Cluster ids are re-assigned every time the source is patched (2 Hz), so the id on a rendered bubble is
      // often stale by the time the worker answers — the leaves came back for a different cluster on another
      // continent. The bubble's position and count are reliable, so take the `point_count` nearest features
      // (in screen space) to the bubble from the same data the source was built from. Instant, never stale.
      const n = nearest.f.properties!.point_count as number;
      const cp = this.map.project(nearest.c);
      const pool: any[] = nearest.f.source === "projectiles" ? this.lastProjectileFeats : this.structFeaturePool();
      const leaves = pool.map((f) => { const q = this.map.project(f.geometry.coordinates); return { f, d: Math.hypot(q.x - cp.x, q.y - cp.y) }; })
        .filter((x) => Number.isFinite(x.d)).sort((x, y) => x.d - y.d).slice(0, n).map((x) => x.f);
      for (const l of leaves) this.pushItem(items, seen, l.properties, e.lngLat);
      const zoom = Math.min(this.map.getZoom() + 2.5, 16); // not getClusterExpansionZoom: same stale-id problem
      zoomTo = () => this.map.easeTo({ center: nearest.c, zoom: Math.max(zoom, this.map.getZoom() + 1) });
      items.sort((a, b) => a.distanceM - b.distanceM);
      this.onPick(items, { x: e.point.x, y: e.point.y }, zoomTo);
      return;
    }
    for (const f of hits) this.pushItem(items, seen, f.properties!, e.lngLat);
    if (items.length === 0) { this.onSelect({ kind: "ground", pos: { lat: e.lngLat.lat, lon: e.lngLat.lng } }); return; }
    items.sort((a, b) => a.distanceM - b.distanceM);
    if (items.length === 1) { this.onSelect({ kind: items[0].kind, id: items[0].id, pos: items[0].pos }); return; }
    this.onPick(items, { x: e.point.x, y: e.point.y }, null);
  }

  private pushItem(items: PickItem[], seen: Set<string>, p: any, at: maplibregl.LngLat) {
    const key = `${p.kind}:${p.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const pos = { lat: p.lat, lon: p.lon };
    const icon = p.kind === "structure" ? (p.stype === "missileSite" ? (p.nuclear ? "marker_missilesitenuke" : "marker_missilesite") : p.stype === "samSite" ? "marker_samsite" : p.stype === "sentryGun" ? "marker_sentry" : "marker_oremine")
      : p.kind === "player" ? (p.dead ? "marker_player_dead" : "marker_player") : p.kind === "missile" ? (p.nuclear ? "marker_missilenuke" : "marker_missile") : p.kind === "interceptor" ? "marker_interceptor" : p.kind === "blueprint" ? (p.icon ?? "marker_oremine") : "marker_loot";
    items.push({ kind: p.kind, id: p.id, pos, label: p.pickLabel ?? p.label ?? p.name ?? p.kind, sub: p.pickSub ?? "", color: p.color ?? "#8b9bb0", distanceM: Math.round(distanceKm({ lat: at.lat, lon: at.lng }, pos) * 1000), icon,
      ...(p.targetLat !== undefined ? { target: { lat: p.targetLat, lon: p.targetLon } } : {}) });
  }

  // ---- data ---------------------------------------------------------------------------------------

  /** Bounds with margin; anything outside is not worth uploading to the GPU every second. */
  private inView(): (p: Pos) => boolean {
    const b = this.map.getBounds();
    const z = this.map.getZoom();
    if (z < 3.5) return () => true; // globe / world view: everything is visible anyway
    const dLat = (b.getNorth() - b.getSouth()) * 0.5, dLon = (b.getEast() - b.getWest()) * 0.5;
    const s = b.getSouth() - dLat, nn = b.getNorth() + dLat, w = b.getWest() - dLon, e = b.getEast() + dLon;
    return (p) => p.lat >= s && p.lat <= nn && p.lon >= w && p.lon <= e;
  }

  /** Static entities — called when structures/players/loot/radiation change. Coalesced to ≤2 Hz. */
  private refreshTimer = 0;
  refresh(force = false) {
    if (!this.ready) return;
    if (force) { this.structFull = true; }
    if (this.refreshTimer) return; // one is already scheduled; it will see the latest state
    const since = performance.now() - this.lastRefreshAt;
    const delay = force ? 0 : Math.max(0, 500 - since);
    this.refreshTimer = window.setTimeout(() => { this.refreshTimer = 0; this.refreshNow(); }, delay);
  }
  private lastRefreshAt = 0;
  /** Models above MODEL_MIN_ZOOM; the sprite icon steps aside (halo stays) so the character is the player. */
  /** Above the player dot (Cam: dot underneath), below projectiles; labels and state overlays are moved above it (Cam: text on top of the models). */
  private addModelsLayer() {
    this.map.addLayer(this.models as any, "projectile-clusters");
    for (const l of ["structures-state", "structures-label", "players-label"]) if (this.map.getLayer(l)) this.map.moveLayer(l, "projectile-clusters");
    this.models.setTheme(this.settings);
    this.enforceModelOrder();
  }

  /**
   * The 3-D models must sit ABOVE every flat marker, or the player's own disc and sprite are painted
   * over the character standing on them — which reads as "there are no player models" (Cam,
   * 2026-09-07: "the circle symbol sits on top of the models ... when I hit refresh it goes under").
   *
   * Stated and enforced here rather than left to emerge from the order of addLayer/moveLayer calls.
   * That order is correct when the whole sequence runs start to finish, and there is no single place
   * that says so, so any path that interrupts it fails silently and only sometimes.
   */
  private enforceModelOrder() {
    if (!this.map.getLayer("models-gl")) return;
    const order = (this.map.getStyle()?.layers ?? []).map((l) => l.id);
    for (const id of markersOverModels(order)) if (this.map.getLayer(id)) this.map.moveLayer(id, "models-gl");
  }

  /** Diagnostics: is every flat marker actually under the models right now, and did the models load? */
  modelStatus() {
    const order = (this.map.getStyle()?.layers ?? []).map((l) => l.id);
    return { present: order.includes("models-gl"), loaded: this.models.ready, built: this.models.builtCount,
      instances: this.models.instances, coveredBy: markersOverModels(order) };
  }
  private syncModels() {
    if (this.models.off || !this.ready) return;
    const show = this.filters.players !== false && this.map.getZoom() >= MODEL_MIN_ZOOM;
    this.models.update(false, this.filters.players !== false, this.filters as any);
    if (this.map.getLayer("players-icon")) this.map.setPaintProperty("players-icon", "icon-opacity", show && this.models.instances > 0 ? 0 : 1);
    // site models replace the flat site icons (the coloured disc stays as the base); sentries keep their sprite for now
    const sitesShown = this.models.structuresVisible && this.models.instances > 0;
    if (this.map.getLayer("structures-icon")) this.map.setPaintProperty("structures-icon", "icon-opacity", sitesShown ? ["case", ["==", ["get", "stype"], "sentryGun"], 1, 0] : 1);
    // rockets are models now: the flat interceptor icon goes, the missile's faint ground shadow stays
    if (this.map.getLayer("interceptors")) this.map.setPaintProperty("interceptors", "icon-opacity", 0);
  }
  private refreshNow() {
    if (!this.ready) return;
    const t0 = performance.now();
    this.lastRefreshAt = t0;
    this.refreshStatic();
    this.refreshRings();
    this.refreshFlights();
    this.syncModels();
    this.perf.refreshMs += performance.now() - t0;
  }
  /** Structures are patched into MapLibre (updateData) rather than rebuilt: 10k features a second was the desktop lag (Cam, 2026-09-03). */
  private structFull = true;
  private structFeature(s: any, me: any, kindLabel: Record<string, string>, kindName: Record<string, string>, owner: (id: number) => string) {
    const r = this.relColor(s.ownerId);
    const f: any = pt(s.pos, { kind: "structure", stype: s.kind, id: s.id, state: s.state, color: r.color, rel: r.rel, mine: me?.id === s.ownerId,
      nuclear: !!s.nuclear, label: mapText(`${kindLabel[s.kind]}${s.nuclear ? "☢" : ""}${s.name ? " " + s.name : ""}`), lat: s.pos.lat, lon: s.pos.lon,
      pickLabel: `${owner(s.ownerId)}'s ${s.nuclear ? "nuclear silo" : kindName[s.kind]}${s.name ? ` "${s.name}"` : ""}`, pickSub: `${s.hp} HP · ${s.state}` });
    f.id = s.id; // updateData needs a top-level feature id
    return f;
  }

  private knownStructIds = new Set<number>();
  /** The features the structures source holds (filters applied), rebuilt on demand for cluster membership. */
  private structFeaturePool(): any[] {
    const me = world.me;
    const kindLabel: Record<string, string> = { missileSite: "MS", samSite: "SAM", sentryGun: "SG", oreMine: "ORE" };
    const kindName: Record<string, string> = { missileSite: "missile site", samSite: "SAM site", sentryGun: "sentry gun", oreMine: "ore mine" };
    const owner = (id: number) => world.players.get(id)?.name ?? `#${id}`;
    const vis = this.inView();
    return [...world.structures.values()].filter((st) => (this.filters as any)[st.kind] !== false && vis(st.pos)).map((st) => this.structFeature(st, me, kindLabel, kindName, owner));
  }
  lastProjectileFeats: any[] = [];
  private lastColourKey = "";
  private refreshStatic() {
    const b = world.balance, me = world.me;
    const kindLabel: Record<string, string> = { missileSite: "MS", samSite: "SAM", sentryGun: "SG", oreMine: "ORE" };
    const kindName: Record<string, string> = { missileSite: "missile site", samSite: "SAM site", sentryGun: "sentry gun", oreMine: "ore mine" };
    const owner = (id: number) => world.players.get(id)?.name ?? `#${id}`;
    this.set("players", fc([...world.players.values()].filter((p) => p.pos && !p.awol && (this.filters.players !== false || p.id === me?.id)).map((p) => {
      const r = this.relColor(p.id);
      return pt(p.pos, { kind: "player", id: p.id, name: mapText(p.name), color: r.color, rel: r.rel, dead: p.dead, lat: p.pos.lat, lon: p.pos.lon, pickLabel: p.name, pickSub: `${p.dead ? "dead" : p.hp + " HP"} · ${p.kills}k/${p.deaths}d` });
    })));
    // OUTSIDE the structures branch below, deliberately. It used to live inside it, and that branch
    // only runs on a full rebuild — first snapshot, a filter/theme change, an alliance colour change.
    // Placing a blueprint takes the incremental path instead, so nothing ever set this source and
    // plans did not appear until something unrelated forced a full rebuild (Cam, 2026-09-05: "I don't
    // think blueprints are putting anything on display on the map"). Capped at 25 per player, so
    // rewriting the whole source every refresh costs nothing.
    // Only ever my own — the server sends nobody else's, so there is nothing here to filter.
    this.set("blueprints", fc([...world.blueprints.values()].map((b: any) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [b.pos.lon, b.pos.lat] },
      properties: {
        // `kind`, `lat`, `lon` and the pick labels are what handleClick/pushItem read. Without them
        // a tap on a plan found nothing and fell through to "ground" (Cam, 2026-09-05: "I cannot
        // click on them to cancel or move them"), because drawing a feature and making it selectable
        // are two separate things and only one of them was done.
        kind: "blueprint", id: b.id, lat: b.pos.lat, lon: b.pos.lon,
        pickLabel: `planned ${b.nuclear ? "nuclear silo" : STRUCT_NAME[b.kind] ?? b.kind}`,
        pickSub: b.blockedReason ? `on hold — ${b.blockedReason}` : "builds when you arrive",
        color: "#4aa3ff",
        // names taken from ICONS above, not invented: a sprite name that does not exist renders
        // nothing at all, with no error, and all five of my first guesses were wrong
        icon: b.kind === "missileSite" ? (b.nuclear ? "marker_missilesitenuke" : "marker_missilesite") : b.kind === "samSite" ? "marker_samsite" : b.kind === "sentryGun" ? "marker_sentry" : "marker_oremine",
        label: b.blockedReason ? "on hold" : "planned",
      },
    }))));
    const visible = (s: any) => (this.filters as any)[s.kind] !== false;
    const src = this.map.getSource("structures") as maplibregl.GeoJSONSource | undefined;
    const colourKey = `${me?.id}|${me?.allianceId}|${me?.requestingJoin}`; // relationship colours depend on these
    if (colourKey !== this.lastColourKey) { this.lastColourKey = colourKey; this.structFull = true; }
    if (world.structuresFull || this.structFull || !src) {
      // everything: first snapshot, filter/theme/cluster change, or an alliance change (colours)
      this.set("structures", fc([...world.structures.values()].filter(visible).map((s) => this.structFeature(s, me, kindLabel, kindName, owner))));
      world.structuresFull = false; this.structFull = false; world.pendingStructures.clear(); world.pendingRemovedStructures.clear();
    } else if (world.pendingStructures.size || world.pendingRemovedStructures.size) {
      const add: any[] = [], remove: (number | string)[] = [];
      for (const id of world.pendingRemovedStructures) remove.push(id);
      for (const id of world.pendingStructures) { const s = world.structures.get(id); if (!s) { remove.push(id); continue; } remove.push(id); if (visible(s)) add.push(this.structFeature(s, me, kindLabel, kindName, owner)); }
      const setChanged = world.pendingRemovedStructures.size > 0 || [...world.pendingStructures].some((id) => !this.knownStructIds.has(id));
      world.pendingStructures.clear(); world.pendingRemovedStructures.clear();
      try { src.updateData({ remove, add }); } catch (e) { this.structFull = true; console.warn("[map] updateData failed, rebuilding", e); }
    }
    for (const id of world.structures.keys()) this.knownStructIds.add(id);
    this.set("loots", fc([...world.loots.values()].map((l) => pt(l.pos, { kind: "loot", id: l.id, value: l.value, lat: l.pos.lat, lon: l.pos.lon, color: "#ffd23f", pickLabel: l.description, pickSub: `£${l.value}` }))));
    this.set("radiation", fc([...world.radiations.values()].map((r) => poly(circle(r.pos, r.radiusKm), { id: r.id }))));
    this.set("radiation-centres", fc([...world.radiations.values()].map((r) => pt(r.pos, { id: r.id }))));
  }

  /** Range rings for sentries and mines — only those in view, only above zoom 10, never in low detail. */
  refreshRings() {
    if (!this.ready) return;
    const b = world.balance;
    const rings: any[] = [];
    if (b && !this.settings.lowDetail && this.map.getZoom() >= 10) {
      const vis = this.inView();
      for (const s of world.structures.values()) {
        if (!vis(s.pos) || (this.filters as any)[s.kind] === false) continue;
        if (s.kind === "sentryGun") rings.push(poly(circle(s.pos, b.combat.sentryGun.rangeKm, 24), { color: this.relColor(s.ownerId).color }));
        if (s.kind === "oreMine") rings.push(poly(circle(s.pos, b.ore.mineRadiusKm, 24), { color: "#ffd23f" }));
      }
      // The same rings for plans (Cam, 2026-09-05: "I'd like the gold ring for blueprint gold mines.
      // And the turret ring for those prints too"). This is the point of planning one: you place it
      // to cover a spot, so you need to see what it will cover BEFORE driving out to build it.
      // Owner-only, because the server sends nobody else's plans — nothing to filter here.
      for (const p of world.blueprints.values()) {
        if (!vis(p.pos) || (this.filters as any)[p.kind] === false) continue;
        if (p.kind === "sentryGun") rings.push(poly(circle(p.pos, b.combat.sentryGun.rangeKm, 24), { color: world.me ? this.relColor(world.me.id).color : "#4aa3ff", planned: true }));
        if (p.kind === "oreMine") rings.push(poly(circle(p.pos, b.ore.mineRadiusKm, 24), { color: "#ffd23f", planned: true }));
      }
    }
    this.set("rings", fc(rings));
  }

  /** Flight paths and areas of effect — rebuilt when the missile set changes or the view moves. */
  /** Ground tracks + areas of effect. Per-missile features are static (launch → target), so after a view change only launches/landings are patched in. */
  private flightIds = new Map<number, string>(); // missile id → target key currently on the map
  refreshFlights(full = false) {
    if (!this.ready) return;
    const t0 = performance.now();
    const b = world.balance, me = world.me;
    if (this.settings.lowDetail) { this.set("arcs", fc([])); this.set("aoe", fc([])); this.flightIds.clear(); this.perf.flightsMs += performance.now() - t0; return; }
    const vis = this.inView();
    const feats = (m: any, target: Pos) => {
      const mineShot = !!me && m.ownerId === me.id;
      const launch: Pos = m.launch ?? m.pos;
      const arcF: any = { type: "Feature", id: m.id, geometry: { type: "LineString", coordinates: arc(launch, target) }, properties: { color: mineShot ? "#3fd47a" : "#ff5a36", mine: mineShot } };
      const aoeF: any[] = [];
      const type = missileType(m.type);
      if (type && b) {
        const blast = type.nuclear ? b.weapons.indices.nukeBlastRadiusKm[type.blastRadiusIndex] : b.weapons.indices.missileBlastRadiusKm[type.blastRadiusIndex];
        aoeF.push({ ...poly(circle(target, blast), { color: mineShot ? "#3fd47a" : "#ff5a36", emp: false }), id: `${m.id}b` });
        if (type.nuclear) aoeF.push({ ...poly(circle(target, blast * b.combat.empRadiusMultiplier), { color: "#ffb347", emp: true }), id: `${m.id}e` });
      }
      return { arcF, aoeF };
    };
    const inView = new Map<number, { m: any; target: Pos; key: string }>();
    for (const m of world.missiles.values()) {
      const target = this.missileTarget(m);
      if (!m.pos || !target) continue;
      const launch: Pos = m.launch ?? m.pos;
      if (!vis(launch) && !vis(target) && !vis(m.pos) && !vis(move(launch, bearing(launch, target), distanceKm(launch, target) / 2))) continue;
      inView.set(m.id, { m, target, key: `${target.lat.toFixed(4)},${target.lon.toFixed(4)}` });
    }
    const arcSrc = this.map.getSource("arcs") as maplibregl.GeoJSONSource | undefined, aoeSrc = this.map.getSource("aoe") as maplibregl.GeoJSONSource | undefined;
    if (full || this.viewDirty || !arcSrc || !aoeSrc || this.flightIds.size === 0) {
      const arcs: any[] = [], aoe: any[] = [];
      this.flightIds.clear();
      for (const [id, v] of inView) { const f = feats(v.m, v.target); arcs.push(f.arcF); aoe.push(...f.aoeF); this.flightIds.set(id, v.key); }
      this.set("arcs", fc(arcs)); this.set("aoe", fc(aoe));
      this.viewDirty = false;
    } else {
      const addA: any[] = [], addB: any[] = [], remA: (number | string)[] = [], remB: (number | string)[] = [];
      for (const [id, key] of this.flightIds) { const v = inView.get(id); if (!v || v.key !== key) { remA.push(id); remB.push(`${id}b`, `${id}e`); this.flightIds.delete(id); } }
      for (const [id, v] of inView) { if (this.flightIds.has(id)) continue; const f = feats(v.m, v.target); addA.push(f.arcF); addB.push(...f.aoeF); this.flightIds.set(id, v.key); }
      if (addA.length || remA.length) {
        try { arcSrc.updateData({ remove: remA, add: addA }); aoeSrc.updateData({ remove: remB, add: addB }); }
        catch (e) { console.warn("[map] flights updateData failed, rebuilding", e); this.flightIds.clear(); this.viewDirty = true; this.refreshFlights(true); return; }
      }
    }
    this.perf.flightsMs += performance.now() - t0;
  }

  private missileTarget(m: any): Pos | null {
    if (m.tracking) { const t = world.players.get(m.targetId); return t?.pos ?? m.target; }
    return m.target;
  }

  private animate = () => {
    this.perf.frames++;
    const wall = performance.now();
    if (this.ready && wall - this.lastOverlayAt >= 33) {
      this.lastOverlayAt = wall;
      const t0 = performance.now();
      try {
        const now = world.now + (wall - lastTick);
        this.overlayDrawn = this.arcs.update(now, this.filters, this.settings);
        if (!this.models.off) this.models.updateFlights(now, this.filters, this.arcs, this.settings);
      } catch (e) { if (!this.overlayErr) { this.overlayErr = true; console.warn("[overlay]", e); } }
      this.perf.overlayMs = (this.perf.overlayMs ?? 0) + (performance.now() - t0);
    }
    if (this.following !== null) this.followTick();
    if (this.followMe) this.followMeTick();
    if (wall - this.perf.since >= 5000) {
      this.perf.fps = Math.round((this.perf.frames * 1000) / (wall - this.perf.since));
      this.lastPerf = { ...this.perf };
      this.perf = { fps: this.perf.fps, refreshMs: 0, flightsMs: 0, animateMs: 0, overlayMs: 0, frames: 0, since: wall };
    }
    // projectile positions are uploaded at ~8 fps — clustering 2 000 points 60× a second was the CPU hog
    if (this.ready && wall - this.lastProjectiles >= (this.settings.lowDetail ? 500 : 125)) {
      this.lastProjectiles = wall;
      const t0 = performance.now();
      const now = world.now + (performance.now() - lastTick);
      const feats: any[] = [];
      const me = world.me;
      // altitude mode: cluster and mark rockets where they APPEAR (the lifted point projected back to the ground under the camera)
      const lifted = this.filters.arcs !== false && !this.settings.lowDetail;
      for (const m of world.missiles.values()) {
        if (!this.filters.missiles) break;
        const f = missileFlight(m, now);
        if (!f) continue;
        const mine = !!me && m.ownerId === me.id;
        let at = f.pos;
        if (lifted) {
          const sp = this.arcs.liftedScreen(m, now);
          // only lift a rocket whose ground point is actually on the near side (its ground projection must round-trip too)
          const gp = this.map.project([f.pos.lon, f.pos.lat]); let groundVisible = false;
          try { const gb = this.map.unproject([gp.x, gp.y]); groundVisible = Math.abs(gb.lat - f.pos.lat) < 0.05 && Math.abs(((gb.lng - f.pos.lon + 540) % 360) - 180) < 0.05; } catch { /* far side */ }
          if (sp && groundVisible) {
            try {
              // a sky point (above the horizon) unprojects to nonsense — accept only if it lands back where it started
              const ll = this.map.unproject([sp.x, sp.y]).wrap();
              const back = this.map.project(ll);
              if (Number.isFinite(ll.lat) && Number.isFinite(ll.lng) && Math.hypot(back.x - sp.x, back.y - sp.y) < 3) at = { lat: ll.lat, lon: ll.lng };
            } catch { /* off the globe */ }
          }
        }
        feats.push(pt(at, { kind: "missile", id: m.id, lat: f.pos.lat, lon: f.pos.lon, color: mine ? "#3fd47a" : "#ff5a36", hostile: !mine, nuclear: !!f.type.nuclear, ecm: !!f.type.ecm, rot: deg(bearing(f.pos, f.target)),
          targetLat: f.target.lat, targetLon: f.target.lon,
          pickLabel: `${world.players.get(m.ownerId)?.name ?? "?"}'s ${f.type.name}`, pickSub: `ETA ${fmtEta(f.etaMs)} · ${Math.round(f.remainingKm)} km to go` }));
      }
      for (const i of world.interceptors.values()) {
        if (!i.pos || !this.filters.interceptors) continue;
        const target = world.missiles.get(i.targetId);
        const owner = world.players.get(i.ownerId)?.name ?? "?";
        const tf = target ? missileFlight(target, now) : null;
        feats.push(pt(i.pos, { kind: "interceptor", id: i.id, lat: i.pos.lat, lon: i.pos.lon, color: "#4aa8ff", hostile: false, rot: tf ? deg(bearing(i.pos, tf.pos)) : 0,
          pickLabel: `${owner}'s interceptor`, pickSub: target ? `chasing ${world.players.get(target.ownerId)?.name ?? "?"}'s missile` : "chasing" }));
      }
      this.set("projectiles", fc(feats));
      this.lastProjectileFeats = feats;
      this.perf.animateMs += performance.now() - t0;
    }
    this.raf = requestAnimationFrame(this.animate);
  };
  lastPerf: typeof this.perf | null = null;

  /** Gold flies from the cache to the collector, then a green "+£value" floats up off their head. */
  flyLoot(from: Pos, to: Pos, value: number) {
    const el = this.map.getContainer();
    const coin = document.createElement("div");
    coin.className = "fx-coin";
    el.appendChild(coin);
    const start = performance.now(), dur = 700;
    const step = () => {
      const t = Math.min(1, (performance.now() - start) / dur), e = 1 - Math.pow(1 - t, 3);
      const a = this.map.project([from.lon, from.lat]), b = this.map.project([to.lon, to.lat]);
      const x = a.x + (b.x - a.x) * e, y = a.y + (b.y - a.y) * e - Math.sin(t * Math.PI) * 40;
      coin.style.transform = `translate(${x}px, ${y}px) scale(${1 - 0.4 * t})`;
      if (t < 1) requestAnimationFrame(step);
      else {
        coin.remove();
        const tag = document.createElement("div");
        tag.className = "fx-money";
        tag.textContent = `+£${value.toLocaleString()}`;
        el.appendChild(tag);
        const s2 = performance.now();
        const rise = () => {
          const u = Math.min(1, (performance.now() - s2) / 1200);
          const p = this.map.project([to.lon, to.lat]);
          tag.style.transform = `translate(${p.x}px, ${p.y - 18 - u * 50}px)`;
          tag.style.opacity = String(1 - u * u);
          if (u < 1) requestAnimationFrame(rise); else tag.remove();
        };
        rise();
        for (let i = 0; i < 6; i++) {
          const bit = document.createElement("div");
          bit.className = "fx-bit";
          el.appendChild(bit);
          const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.6, sp = 40 + Math.random() * 50, s3 = performance.now();
          const go = () => {
            const u = Math.min(1, (performance.now() - s3) / 900);
            const p = this.map.project([to.lon, to.lat]);
            bit.style.transform = `translate(${p.x + Math.cos(ang) * sp * u}px, ${p.y + Math.sin(ang) * sp * u + 30 * u * u}px)`;
            bit.style.opacity = String(1 - u);
            if (u < 1) requestAnimationFrame(go); else bit.remove();
          };
          go();
        }
      }
    };
    step();
  }

  /** The selected object drawn on top of everything (glow + marker + icon + label); null clears it. */
  setSelected(sel: { pos: Pos; color: string; icon: string; label: string } | null) {
    this.set("selected", sel ? fc([pt(sel.pos, { color: sel.color, icon: sel.icon, label: mapText(sel.label) })]) : fc([]));
  }
  showSelection(pos: Pos | null, radiusKm: number) {
    this.set("selection", pos ? fc([poly(circle(pos, radiusKm), {})]) : fc([]));
  }

  /** Hide/show the basemap's country-name layers (their ids contain "country" in every OpenFreeMap style). */
  setCountryLabels(on: boolean) {
    const apply = () => { for (const l of this.map.getStyle()?.layers ?? []) if (/country/i.test(l.id)) this.map.setLayoutProperty(l.id, "visibility", on ? "visible" : "none"); };
    if (this.map.isStyleLoaded()) apply(); else this.map.once("style.load", apply);
    this.countryLabels = on;
  }
  private countryLabels = true;

  flyTo(p: Pos, zoom = 12, pitch?: number) { this.map.flyTo({ center: [p.lon, p.lat], zoom, ...(pitch !== undefined ? { pitch } : {}) }); }
  centre(): Pos { const c = this.map.getCenter(); return { lat: c.lat, lon: c.lng }; }
  zoom(): number { return this.map.getZoom(); }

  /**
   * Point the local window at the player's game position, or turn it off with `null`.
   *
   * Only a move worth more than a few km reloads tiles. Refreshing on every GPS fix would refetch
   * the basemap continuously and give the server a transform to do for each one — the window has to
   * follow the player, but not at the rate their phone reports.
   */
  setLocalWindow(pos: Pos | null, token: string | null) {
    const had = !!localWin;
    if (!pos || !token) {
      localWin = null;
      this.lastTileReloadAt = null;
      if (had) this.reloadBasemapTiles();
      return;
    }
    localWin = { pos, token };
    // Compare against where the tiles were LAST RELOADED, not against the previous fix.
    //
    // The first version compared `was.pos` — the previous fix — which a driving phone updates every
    // few seconds and a few hundred metres. `> 5 km` between consecutive fixes is essentially never
    // true, so after the initial load the tiles were never refreshed again no matter how far the
    // player travelled: the window stopped following them, which is the whole job of this method.
    const from = this.lastTileReloadAt;
    if (!from || distanceKm(from, pos) > 5) {
      this.lastTileReloadAt = pos;
      this.reloadBasemapTiles();
    }
  }
  /** Where the basemap was last dropped, so travel accumulates instead of resetting every fix. */
  private lastTileReloadAt: Pos | null = null;

  /** Drop cached basemap tiles so the window's edge follows the player. */
  private reloadBasemapTiles() {
    const sources = this.map.getStyle()?.sources ?? {};
    for (const [id, s] of Object.entries(sources)) {
      if ((s as any)?.type !== "vector") continue;
      try { (this.map as any).refreshTiles?.(id); } catch { /* style still loading; the next move retries */ }
    }
  }
  destroy() { cancelAnimationFrame(this.raf); this.map.remove(); }
}

let lastTick = performance.now();
export function markTick() { lastTick = performance.now(); }
export function nowEstimate() { return world.now + (performance.now() - lastTick); }

/** Where a missile is right now (client estimate), where it's going, and when it gets there. */
export function missileFlight(m: any, now = nowEstimate()) {
  const type = missileType(m.type);
  const target: Pos | null = m.tracking ? (world.players.get(m.targetId)?.pos ?? m.target) : m.target;
  if (!type || !target || !m.pos || !world.balance) return null;
  const speed = world.balance.weapons.indices.missileSpeedKph[type.speedIndex];
  const km = Math.max(0, (speed / 3_600_000) * (now - m.seenAt));
  const total = distanceKm(m.pos, target);
  const pos = km >= total ? target : move(m.pos, bearing(m.pos, target), km);
  const remainingKm = Math.max(0, total - km);
  return { type, pos, target, remainingKm, etaMs: (remainingKm / speed) * 3_600_000 };
}
export const fmtEta = (ms: number) => ms < 1000 ? "impact" : ms < 60_000 ? `${Math.ceil(ms / 1000)}s` : ms < 3_600_000 ? `${Math.floor(ms / 60_000)}m ${Math.ceil((ms % 60_000) / 1000)}s` : `${(ms / 3_600_000).toFixed(1)}h`;

// ---- basemap thumbnails ---------------------------------------------------------------------------

/**
 * Snapshot each basemap at the view the player is looking at, for the Maps tab.
 *
 * ONE offscreen map, cycling styles, rather than one live map per thumbnail: a browser allows only
 * a handful of WebGL contexts and the real map already holds one, so five more would start evicting
 * — including, sometimes, the game's.
 *
 * `preserveDrawingBuffer` is mandatory. Without it the GL buffer is cleared after each frame and
 * `toDataURL` hands back a blank image with no error at all.
 */
const thumbCache = new Map<string, string>();
let thumbBusy: Promise<void> | null = null;

/** Cache key: the view rounded, so small pans reuse the picture instead of re-rendering five maps. */
const thumbKey = (id: string, c: Pos, zoom: number) =>
  `${id}@${c.lat.toFixed(2)},${c.lon.toFixed(2)}z${Math.round(zoom)}`;

export function cachedThumb(id: string, c: Pos, zoom: number): string | undefined {
  return thumbCache.get(thumbKey(id, c, zoom));
}

/**
 * Render any missing thumbnails for this view. Serialised: a second call while one is running waits,
 * so a player flicking the tab open and shut cannot stack offscreen maps.
 */
export async function renderThumbs(ids: string[], c: Pos, zoom: number, onEach?: () => void): Promise<void> {
  while (thumbBusy) await thumbBusy;
  const missing = ids.filter((id) => !thumbCache.has(thumbKey(id, c, zoom)));
  if (!missing.length) return;
  let done!: () => void;
  thumbBusy = new Promise<void>((r) => (done = r));
  const host = document.createElement("div");
  host.style.cssText = "position:absolute;left:-10000px;top:0;width:320px;height:200px;pointer-events:none";
  document.body.appendChild(host);
  let m: MLMap | null = null;
  try {
    m = new maplibregl.Map({
      container: host, style: basemapUrl(missing[0]), center: [c.lon, c.lat], zoom,
      attributionControl: false, interactive: false, preserveDrawingBuffer: true, fadeDuration: 0,
    });
    for (const id of missing) {
      if (m.getStyle() && basemapUrl(id) !== (m as any)._nukiStyle) { m.setStyle(basemapUrl(id)); (m as any)._nukiStyle = basemapUrl(id); }
      await new Promise<void>((res) => {
        let settled = false;
        const finish = () => { if (settled) return; settled = true; res(); };
        m!.once("idle", finish);
        setTimeout(finish, 4000); // a style that never idles must not hang the tab
      });
      try { thumbCache.set(thumbKey(id, c, zoom), m.getCanvas().toDataURL("image/jpeg", 0.72)); } catch { /* tainted or lost context: skip this one */ }
      onEach?.();
    }
  } catch { /* offscreen render is best-effort; the tab still lists the maps by name */ }
  finally {
    try { m?.remove(); } catch { /* already gone */ }
    host.remove();
    done();
    thumbBusy = null;
  }
}
